import React, { useState, useRef, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { 
  FileText, 
  Upload, 
  Download, 
  CheckCircle2, 
  XCircle, 
  AlertCircle, 
  Trash2, 
  ArrowRight,
  ShieldCheck,
  Search,
  History,
  Clock,
  Check,
  X,
  CreditCard,
  Building2,
  FileSpreadsheet
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Spinner from '../components/ui/Spinner';
import Modal from '../components/ui/Modal';
import toast from 'react-hot-toast';
import { PERMISSIONS } from '../utils/permissions';
import HasPermission from '../components/HasPermission';

const transferSetupSchema = z.object({
  accountId: z.string().min(1, 'Please select an account'),
  description: z.string().min(1, 'Description is required'),
});

const approvalPinSchema = z.object({
  pin: z.string().length(4, 'PIN must be 4 digits'),
});

const approvalOtpSchema = z.object({
  otp: z.string().length(6, 'OTP must be 6 digits'),
});

const rejectSchema = z.object({
  reason: z.string().min(1, 'Please provide a reason for rejection'),
});

// --- MOCK DATA ---
const dummyAccounts = [
  { id: '1', name: 'Main Operating Account', balance: '₦12,450,000.00', number: '0123456789' },
  { id: '2', name: 'Treasury Account', balance: '₦450,000,000.00', number: '9876543210' },
];

const pendingRequests = [
  {
    id: 'BR-001',
    sender: 'John Doe (Finance)',
    description: 'October Staff Salaries',
    count: 145,
    totalValue: 24500000,
    timestamp: '2026-03-05T14:30:00Z',
    items: [
      { id: 'item-1', name: 'Alice Smith', account: '0011223344', bank: 'Waya Bank', amount: 150000 },
      { id: 'item-2', name: 'Bob Johnson', account: '9988776655', bank: 'GTBank', amount: 120000 },
      { id: 'item-3', name: 'Charlie Davis', account: '5544332211', bank: 'Access Bank', amount: 200000 },
    ]
  },
  {
    id: 'BR-002',
    sender: 'Sarah Wilson (Operations)',
    description: 'Vendor Payments - Q1',
    count: 24,
    totalValue: 4200000,
    timestamp: '2026-03-06T09:15:00Z',
    items: [
      { id: 'item-4', name: 'Office Supplies Inc', account: '1122334455', bank: 'Zenith Bank', amount: 450000 },
      { id: 'item-5', name: 'Cloud Services Ltd', account: '6677889900', bank: 'Utility Bank', amount: 1250000 },
    ]
  }
];

const formatCurrency = (val) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(val);

// --- COMPONENTS ---

const BulkTransferPage = () => {
  const [activeTab, setActiveTab] = useState('initiate'); // 'initiate' | 'approvals' | 'history'
  
  // Initiation State
  const [initiateStep, setInitiateStep] = useState(1); // 1: Setup, 2: Review, 3: Success
  const [uploadedTransactions, setUploadedTransactions] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [requestsList, setRequestsList] = useState(pendingRequests);
  const [historyItems, setHistoryItems] = useState([]);

  // Approval State
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [approvalSubStep, setApprovalSubStep] = useState('pin'); // 'pin' | 'otp'
  const [isProcessing, setIsProcessing] = useState(false);

  // --- Forms ---
  
  // Initiation Form
  const {
    register: registerSetup,
    handleSubmit: handleSubmitSetup,
    setValue: setSetupValue,
    watch: watchSetup,
    reset: resetSetup,
    formState: { errors: setupErrors },
  } = useForm({
    resolver: zodResolver(transferSetupSchema),
    mode: 'onChange',
    defaultValues: { accountId: '', description: '' },
  });
  
  const selectedAccountId = watchSetup('accountId');

  // Approval PIN Form
  const {
    register: registerPin,
    handleSubmit: handleSubmitPin,
    reset: resetPin,
    formState: { errors: pinErrors },
  } = useForm({
    resolver: zodResolver(approvalPinSchema),
    mode: 'onChange',
    defaultValues: { pin: '' },
  });

  // Approval OTP Form
  const {
    register: registerOtp,
    handleSubmit: handleSubmitOtp,
    reset: resetOtp,
    formState: { errors: otpErrors },
  } = useForm({
    resolver: zodResolver(approvalOtpSchema),
    mode: 'onChange',
    defaultValues: { otp: '' },
  });

  // Rejection Form
  const {
    register: registerReject,
    handleSubmit: handleSubmitReject,
    reset: resetReject,
    formState: { errors: rejectErrors },
  } = useForm({
    resolver: zodResolver(rejectSchema),
    mode: 'onChange',
    defaultValues: { reason: '' },
  });

  // --- Handlers: Initiation ---
  
  const handleDownloadTemplate = () => {
    const headers = 'Recipient Name,Account Number,Bank Code,Amount,Narration\n';
    const blob = new Blob([headers], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'waya_bulk_transfer_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Template downloaded');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    // Simulate parsing
    setTimeout(() => {
      const mockData = [
        { id: Math.random(), name: 'Tunde Afolayan', account: '0459345231', bank: 'Waya Bank', amount: 45000, status: 'valid' },
        { id: Math.random(), name: 'Bose Adeyemi', account: '0034958234', bank: 'GTBank', amount: 12500, status: 'valid' },
        { id: Math.random(), name: 'Chukwudi Obi', account: '0234567891', bank: 'Zenith Bank', amount: 89700.50, status: 'valid' },
        { id: Math.random(), name: 'Fatima Musa', account: '0987654321', bank: 'Access Bank', amount: 15000, status: 'invalid', message: 'Invalid Account Number' },
        { id: Math.random(), name: 'Emanuel Ekong', account: '0112233445', bank: 'UBA', amount: 33200, status: 'valid' },
      ];
      setUploadedTransactions(mockData);
      setIsUploading(false);
      setInitiateStep(2);
      toast.success('File uploaded and parsed');
    }, 1500);
  };

  const removeTransaction = (id) => {
    setUploadedTransactions(prev => prev.filter(t => t.id !== id));
  };

  const stats = useMemo(() => {
    const valid = uploadedTransactions.filter(t => t.status === 'valid');
    const total = valid.reduce((acc, curr) => acc + curr.amount, 0);
    return { count: valid.length, total };
  }, [uploadedTransactions]);

  const handleSendForApproval = () => {
    if (uploadedTransactions.length === 0) return toast.error('No transactions to send');
    
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setInitiateStep(3);
    }, 2000);
  };

  const resetInitiation = () => {
    setInitiateStep(1);
    setUploadedTransactions([]);
    resetSetup();
  };

  // --- Handlers: Approvals ---

  const handleOpenRequest = (req) => {
    setSelectedRequest(JSON.parse(JSON.stringify(req))); // Deep copy
  };

  const removeDetailItem = (id) => {
    if (!selectedRequest) return;
    const updatedItems = selectedRequest.items.filter(i => i.id !== id);
    const updatedValue = updatedItems.reduce((acc, curr) => acc + curr.amount, 0);
    setSelectedRequest({
      ...selectedRequest,
      items: updatedItems,
      count: updatedItems.length,
      totalValue: updatedValue
    });
  };

  const onPinSubmit = (data) => {
    setApprovalSubStep('otp');
    toast.success('OTP sent to your email');
  };

  const onOtpSubmit = (data) => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setIsApprovalModalOpen(false);
      setHistoryItems(prev => [{ ...selectedRequest, status: 'Approved', processedAt: new Date().toISOString() }, ...prev]);
      setRequestsList(prev => prev.filter(r => r.id !== selectedRequest.id));
      setSelectedRequest(null);
      resetPin();
      resetOtp();
      toast.success(`Bulk Request ${selectedRequest.id} approved successfully!`);
    }, 2000);
  };

  const onRejectSubmit = (data) => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setIsRejectModalOpen(false);
      setHistoryItems(prev => [{ ...selectedRequest, status: 'Rejected', reason: data.reason, processedAt: new Date().toISOString() }, ...prev]);
      setRequestsList(prev => prev.filter(r => r.id !== selectedRequest.id));
      setSelectedRequest(null);
      resetReject();
      toast.success('Request rejected successfully');
    }, 1500);
  };

  // --- RENDERING ---

  return (
    <div className="space-y-6">
      {/* Header & Tabs */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Bulk Transfer</h1>
          <p className="text-sm text-slate-500">Manage large scale payments efficiently.</p>
        </div>
        <div className="flex p-1 bg-slate-100 rounded-xl w-fit">
          <button 
            onClick={() => setActiveTab('initiate')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeTab === 'initiate' ? 'bg-white text-primary shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Initiate Transfer
          </button>
          <button 
            onClick={() => setActiveTab('approvals')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeTab === 'approvals' ? 'bg-white text-primary shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Pending Approvals
          </button>
          <button 
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeTab === 'history' ? 'bg-white text-primary shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            History
          </button>
        </div>
      </div>

      {activeTab === 'initiate' && (
        <div className="space-y-6">
          {initiateStep === 1 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Setup Column */}
              <div className="lg:col-span-2 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Transfer Setup</CardTitle>
                    <CardDescription>Select account and provide a description for this batch.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                       <label className="text-sm font-medium text-slate-700">Debit Account</label>
                       <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                         {dummyAccounts.map(acc => (
                           <div 
                            key={acc.id}
                            onClick={() => setSetupValue('accountId', acc.id, { shouldValidate: true })}
                            className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${selectedAccountId === acc.id ? 'border-primary bg-primary/5' : 'border-slate-100 hover:border-slate-200 bg-white'}`}
                           >
                              <input type="hidden" value={acc.id} {...registerSetup('accountId')} />
                             <div className="flex justify-between items-start mb-2">
                               <Building2 size={20} className={selectedAccountId === acc.id ? 'text-primary' : 'text-slate-400'} />
                               {selectedAccountId === acc.id && <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center text-white"><Check size={12} /></div>}
                             </div>
                             <p className="text-sm font-semibold truncate">{acc.name}</p>
                             <p className="text-xs text-slate-500">{acc.number}</p>
                             <p className="text-sm font-bold text-slate-900 mt-2">{acc.balance}</p>
                           </div>
                         ))}
                       </div>
                       {setupErrors.accountId && <p className="text-xs text-red-500 mt-1">{setupErrors.accountId.message}</p>}
                    </div>
                    <div className="space-y-2">
                      <Input 
                        label="Description"
                        placeholder="e.g. Staff Salaries - March 2026" 
                        {...registerSetup('description')}
                        error={setupErrors.description?.message}
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-dashed border-2 bg-slate-50 hover:bg-slate-100/50 transition-colors">
                  <CardContent className="p-10 flex flex-col items-center justify-center text-center space-y-4">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                      {isUploading ? <Spinner size="lg" /> : <Upload size={32} />}
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-slate-900">Upload Bulk File</h4>
                      <p className="text-sm text-slate-500 max-w-sm">
                        Drag and drop your filled template here, or click to browse files. Supported formats: .csv, .xlsx
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button variant="primary" onClick={handleSubmitSetup(() => fileInputRef.current?.click())} isLoading={isUploading}>
                        Select File
                      </Button>
                      <input 
                        type="file" 
                        ref={fileInputRef} 
                        onChange={handleFileUpload} 
                        className="hidden" 
                        accept=".csv,.xlsx"
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Resource Column */}
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Instructions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex gap-3">
                      <div className="shrink-0 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold">1</div>
                      <p className="text-sm text-slate-600">Download the transfer template to your device.</p>
                    </div>
                    <div className="flex gap-3">
                      <div className="shrink-0 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold">2</div>
                      <p className="text-sm text-slate-600">Fill in recipient details, amounts, and bank codes correctly.</p>
                    </div>
                    <div className="flex gap-3">
                      <div className="shrink-0 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold">3</div>
                      <p className="text-sm text-slate-600">Upload the file and review for any validation errors.</p>
                    </div>
                    <div className="pt-4 border-t border-slate-100">
                      <HasPermission permission={PERMISSIONS.DOWNLOADS}>
                        <Button variant="secondary" className="w-full gap-2" onClick={handleDownloadTemplate}>
                          <Download size={18} /> Download Template
                        </Button>
                      </HasPermission>
                    </div>
                  </CardContent>
                </Card>

                <div className="bg-amber-50 rounded-xl p-4 border border-amber-100 flex gap-3">
                  <AlertCircle className="text-amber-500 shrink-0" size={20} />
                  <div className="text-xs text-amber-800 space-y-1">
                    <p className="font-bold uppercase tracking-wider text-[10px]">Important Note</p>
                    <p>Ensure you have sufficient balance in the selected account before initiating a bulk transfer to avoid processing delays.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {initiateStep === 2 && (
            <div className="space-y-6 animate-fade-in">
              {/* Summary Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center"><FileText size={20} /></div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Batch Description</p>
                      <p className="text-sm font-bold truncate">{watchSetup('description') || 'No description'}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center"><ArrowRight size={20} /></div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Total Volume</p>
                      <p className="text-sm font-bold">{stats.count} Transactions</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-green-100 text-green-600 flex items-center justify-center"><CheckCircle2 size={20} /></div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Total Value</p>
                      <p className="text-sm font-bold text-green-600">{formatCurrency(stats.total)}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Transaction Table */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Review Transactions</CardTitle>
                    <CardDescription>Verify all entries before sending for approval.</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="secondary" size="sm" onClick={() => setInitiateStep(1)}>Back</Button>
                    <Button variant="primary" size="sm" onClick={handleSendForApproval} isLoading={isProcessing}>Send for Approval</Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                   <table className="w-full text-left text-sm">
                     <thead>
                       <tr className="bg-slate-50 border-y border-slate-100 text-slate-500">
                         <th className="px-6 py-3 font-semibold">Recipient</th>
                         <th className="px-6 py-3 font-semibold">Account Info</th>
                         <th className="px-6 py-3 font-semibold text-right">Amount</th>
                         <th className="px-6 py-3 font-semibold">Status</th>
                         <th className="px-6 py-3 font-semibold text-center">Action</th>
                       </tr>
                     </thead>
                     <tbody className="divide-y divide-slate-100">
                       {uploadedTransactions.map((tx) => (
                         <tr key={tx.id} className="hover:bg-slate-50/50 transition-colors">
                           <td className="px-6 py-4 font-medium text-slate-900">{tx.name}</td>
                           <td className="px-6 py-4">
                             <p className="text-slate-700">{tx.bank}</p>
                             <p className="text-xs text-slate-400">{tx.account}</p>
                           </td>
                           <td className="px-6 py-4 text-right font-semibold">{formatCurrency(tx.amount)}</td>
                           <td className="px-6 py-4">
                             {tx.status === 'valid' ? (
                               <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-green-50 text-green-700 border border-green-100">
                                 <Check size={10} /> Valid
                               </span>
                             ) : (
                               <div className="flex flex-col">
                                 <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-50 text-red-700 border border-red-100 w-fit">
                                   <X size={10} /> Error
                                 </span>
                                 <span className="text-[10px] text-red-500 mt-1">{tx.message}</span>
                               </div>
                             )}
                           </td>
                           <td className="px-6 py-4 text-center">
                             <button 
                               onClick={() => removeTransaction(tx.id)}
                               className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                             >
                               <Trash2 size={16} />
                             </button>
                           </td>
                         </tr>
                       ))}
                     </tbody>
                   </table>
                </CardContent>
              </Card>
            </div>
          )}

          {initiateStep === 3 && (
            <Card className="max-w-xl mx-auto text-center p-10 space-y-6 animate-scale-in">
              <div className="w-20 h-20 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-4">
                <ShieldCheck size={40} />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-bold text-slate-900">Request Sent for Approval</h2>
                <p className="text-slate-500">
                  Your bulk transfer request has been successfully queued. The account owner will review and process the transaction.
                </p>
              </div>
              <div className="bg-slate-50 p-4 rounded-xl flex items-center justify-between text-sm text-left border border-slate-100">
                 <div>
                   <p className="text-slate-400 mb-1">Total Value</p>
                   <p className="font-bold text-slate-900">{formatCurrency(stats.total)}</p>
                 </div>
                 <div className="text-right">
                   <p className="text-slate-400 mb-1">Reference</p>
                   <p className="font-mono text-xs font-bold text-slate-900">BT-REQ-{Math.floor(100000 + Math.random() * 900000)}</p>
                 </div>
              </div>
              <div className="flex gap-3">
                <Button variant="secondary" className="flex-1" onClick={resetInitiation}>Create Another</Button>
                <Button variant="primary" className="flex-1" onClick={() => setActiveTab('history')}>View Summary</Button>
              </div>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'approvals' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fade-in h-[calc(100vh-250px)]">
          {/* Requests List */}
          <div className="lg:col-span-1 border-r border-slate-200 pr-2 overflow-y-auto space-y-4">
             <h3 className="font-semibold text-slate-900 px-2 mb-2">Queue ({requestsList.length})</h3>
             {requestsList.length === 0 ? (
               <div className="text-center p-8 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                 <p className="text-slate-500 text-sm">No pending requests</p>
               </div>
             ) : (
               requestsList.map(req => (
               <div 
                key={req.id}
                onClick={() => handleOpenRequest(req)}
                className={`p-4 rounded-xl border cursor-pointer transition-all ${selectedRequest?.id === req.id ? 'border-primary bg-primary/5 shadow-sm' : 'border-slate-100 hover:border-slate-200 bg-white'}`}
               >
                 <div className="flex justify-between items-start mb-2">
                   <div className="p-2 rounded-lg bg-slate-100 text-slate-600"><FileSpreadsheet size={16} /></div>
                   <span className="text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-bold uppercase">Pending</span>
                 </div>
                 <h4 className="text-sm font-bold text-slate-900 truncate">{req.description}</h4>
                 <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Clock size={12} /> {new Date(req.timestamp).toLocaleDateString()}</p>
                 <div className="mt-4 pt-3 border-t border-dotted border-slate-200 flex justify-between items-end">
                    <div>
                      <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Amount</p>
                      <p className="text-sm font-bold text-slate-900">{formatCurrency(req.totalValue)}</p>
                    </div>
                    <p className="text-xs font-medium text-slate-500">{req.count} Items</p>
                 </div>
               </div>
             )))}
          </div>

          {/* Details View */}
          <div className="lg:col-span-2 overflow-hidden flex flex-col">
            {selectedRequest ? (
              <div className="flex flex-col h-full animate-fade-in">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">{selectedRequest.description}</h2>
                    <p className="text-sm text-slate-500 mt-1">Request ID: {selectedRequest.id} • Sent by {selectedRequest.sender}</p>
                  </div>
                  <div className="flex gap-2">
                     <Button variant="danger" size="sm" onClick={() => { resetReject(); setIsRejectModalOpen(true); }}>Reject All</Button>
                     <Button variant="primary" size="sm" onClick={() => { resetPin(); resetOtp(); setApprovalSubStep('pin'); setIsApprovalModalOpen(true); }}>Approve All</Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                   <div className="bg-white p-4 rounded-xl border border-slate-100 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Transaction Count</p>
                        <p className="text-lg font-bold text-slate-900">{selectedRequest.count}</p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-400"><History size={20} /></div>
                   </div>
                   <div className="bg-white p-4 rounded-xl border border-slate-100 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Total Value</p>
                        <p className="text-lg font-bold text-primary">{formatCurrency(selectedRequest.totalValue)}</p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary"><CreditCard size={20} /></div>
                   </div>
                </div>

                <div className="flex-1 overflow-auto bg-white border border-slate-100 rounded-xl">
                   <table className="w-full text-left text-sm">
                     <thead className="sticky top-0 bg-slate-50 border-b border-slate-100 z-10">
                       <tr className="text-slate-500">
                         <th className="px-6 py-3 font-semibold">Recipient</th>
                         <th className="px-6 py-3 font-semibold">Bank Details</th>
                         <th className="px-6 py-3 font-semibold text-right">Amount</th>
                         <th className="px-6 py-3 font-semibold text-center whitespace-nowrap">Review Action</th>
                       </tr>
                     </thead>
                     <tbody className="divide-y divide-slate-100">
                        {selectedRequest.items.map(item => (
                          <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-6 py-4 font-medium text-slate-900">{item.name}</td>
                            <td className="px-6 py-4">
                              <p className="text-slate-700">{item.bank}</p>
                              <p className="text-xs text-slate-400">{item.account}</p>
                            </td>
                            <td className="px-6 py-4 text-right font-bold">{formatCurrency(item.amount)}</td>
                            <td className="px-6 py-4 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <button className="p-1.5 text-green-500 hover:bg-green-50 rounded-lg transition-colors border border-transparent hover:border-green-200">
                                  <Check size={16} />
                                </button>
                                <button 
                                  onClick={() => removeDetailItem(item.id)}
                                  className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-200"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                     </tbody>
                   </table>
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center p-10 bg-slate-50/50 border-2 border-dashed border-slate-200 rounded-3xl">
                <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center text-slate-300 mb-4 animate-pulse">
                  <Search size={32} />
                </div>
                <h3 className="text-xl font-bold text-slate-400">No Request Selected</h3>
                <p className="text-slate-400 max-w-xs mt-2">Select a bulk transfer request from the left sidebar to start your review.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-6 animate-fade-in min-h-[400px]">
          {historyItems.length === 0 ? (
            <Card className="flex flex-col items-center justify-center text-center p-10 h-[400px]">
              <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center text-slate-300 mb-6">
                  <History size={40} />
              </div>
              <h3 className="text-xl font-bold text-slate-900">No Processed History</h3>
              <p className="text-slate-500 mt-2 max-w-sm">Completed bulk transfers and their processing status will appear here once you've completed some transactions.</p>
              <Button variant="secondary" className="mt-6" onClick={() => setActiveTab('initiate')}>Back to Initiation</Button>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Processing History</CardTitle>
                <CardDescription>View all approved and rejected bulk transfer batches.</CardDescription>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 border-y border-slate-100">
                    <tr className="text-slate-500">
                      <th className="px-6 py-3 font-semibold">Batch ID & Desc</th>
                      <th className="px-6 py-3 font-semibold">Processed Date</th>
                      <th className="px-6 py-3 font-semibold text-right">Volume / Value</th>
                      <th className="px-6 py-3 font-semibold text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {historyItems.map(item => (
                      <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-6 py-4">
                          <p className="font-bold text-slate-900">{item.description}</p>
                          <p className="text-xs text-slate-500 font-mono mt-0.5">{item.id}</p>
                        </td>
                        <td className="px-6 py-4 text-slate-600">
                          {new Date(item.processedAt).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <p className="font-bold text-slate-900">{formatCurrency(item.totalValue)}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{item.count} items</p>
                        </td>
                        <td className="px-6 py-4 text-center">
                          {item.status === 'Approved' ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-green-50 text-green-700 border border-green-100">
                              <CheckCircle2 size={12} /> Approved
                            </span>
                          ) : (
                            <div className="flex flex-col items-center">
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-red-50 text-red-700 border border-red-100">
                                <XCircle size={12} /> Rejected
                              </span>
                              {item.reason && <span className="text-[10px] text-red-500 mt-1 max-w-[150px] truncate" title={item.reason}>{item.reason}</span>}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Approval Sequence Modal */}
      <Modal 
        isOpen={isApprovalModalOpen} 
        onClose={() => setIsApprovalModalOpen(false)}
        title="Approve Bulk Transfer"
        description={selectedRequest?.description}
      >
        <div className="space-y-6 pt-2">
          {/* Progress Indicators */}
          <div className="flex items-center justify-center mb-6 max-w-xs mx-auto">
             {['pin', 'otp'].map((step, idx) => (
               <React.Fragment key={step}>
                 <div className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center font-bold text-sm ${
                   approvalSubStep === step ? 'bg-primary text-white scale-110 shadow-lg' : 
                   (['pin', 'otp'].indexOf(approvalSubStep) > idx ? 'bg-green-500 text-white' : 'bg-slate-100 text-slate-400')
                 } transition-all duration-300`}>
                    {['pin', 'otp'].indexOf(approvalSubStep) > idx ? <Check size={18} /> : idx + 1}
                 </div>
                 {idx < 1 && <div className={`flex-1 h-1 mx-4 rounded-full ${['pin', 'otp'].indexOf(approvalSubStep) > idx ? 'bg-green-500' : 'bg-slate-100'}`}></div>}
               </React.Fragment>
             ))}
          </div>

          <div className="bg-slate-50 p-4 rounded-xl flex items-center justify-between mb-4">
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">Total Amount</p>
              <p className="text-lg font-bold text-slate-900">{selectedRequest && formatCurrency(selectedRequest.totalValue)}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">Batch Count</p>
              <p className="text-lg font-bold text-slate-900">{selectedRequest?.count}</p>
            </div>
          </div>

          {approvalSubStep === 'pin' && (
             <form id="approval-pin-form" onSubmit={handleSubmitPin(onPinSubmit)} className="space-y-4 animate-fade-in">
                <div className="space-y-2">
                   <Input 
                    label="Transaction PIN"
                    type="password" 
                    maxLength={4}
                    placeholder="Enter 4-digit PIN" 
                    className="text-center tracking-[1em] text-2xl placeholder:tracking-normal placeholder:text-base placeholder:font-normal"
                    {...registerPin('pin')}
                    error={pinErrors.pin?.message}
                  />
                </div>
                <p className="text-xs text-slate-500 text-center">Enter your private transaction PIN</p>
             </form>
          )}

          {approvalSubStep === 'otp' && (
             <form id="approval-otp-form" onSubmit={handleSubmitOtp(onOtpSubmit)} className="space-y-4 animate-fade-in">
                <div className="text-center space-y-2 mb-4">
                  <p className="text-sm text-slate-600">A verification code has been sent to</p>
                  <p className="font-bold text-slate-900">090*****456</p>
                </div>
                <div className="space-y-2">
                  <Input 
                    label="OTP Code"
                    type="text" 
                    maxLength={6}
                    placeholder="000000" 
                    className="text-center tracking-[0.5em] text-2xl placeholder:tracking-normal placeholder:text-base placeholder:font-normal"
                    {...registerOtp('otp')}
                    error={otpErrors.otp?.message}
                  />
                </div>
                <button type="button" className="text-xs text-primary font-bold w-full text-center hover:underline">Resend Code</button>
             </form>
          )}

          <div className="flex gap-3 mt-8">
            <Button variant="secondary" className="flex-1" onClick={() => setIsApprovalModalOpen(false)}>Cancel</Button>
            <Button 
              variant="primary" 
              className="flex-1" 
              type="submit"
              form={approvalSubStep === 'pin' ? 'approval-pin-form' : 'approval-otp-form'}
              isLoading={isProcessing}
            >
              {approvalSubStep === 'otp' ? 'Confirm & Process' : 'Continue'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal 
        isOpen={isRejectModalOpen} 
        onClose={() => setIsRejectModalOpen(false)}
        title="Reject Bulk Transfer"
        description={`Rejecting ${selectedRequest?.id}`}
      >
        <form id="reject-form" onSubmit={handleSubmitReject(onRejectSubmit)} className="space-y-6 pt-2">
          <div className="bg-red-50 p-4 rounded-xl flex items-start gap-4 mb-4 border border-red-100">
            <AlertCircle className="text-red-500 shrink-0 mt-0.5" size={20} />
            <div className="text-sm text-red-800">
              <p className="font-bold">Are you sure you want to reject this request?</p>
              <p className="mt-1">This action cannot be undone. The initiator will be notified of the rejection.</p>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Reason for Rejection <span className="text-red-500">*</span></label>
            <textarea 
              className={`w-full p-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none text-sm placeholder:text-slate-400 ${rejectErrors.reason ? 'border-red-500' : 'border-slate-200'}`}
              rows={4}
              placeholder="Please provide a reason to help the initiator understand why this request was rejected..."
              {...registerReject('reason')}
            ></textarea>
            {rejectErrors.reason && <p className="text-xs text-red-500 mt-1">{rejectErrors.reason.message}</p>}
          </div>

          <div className="flex gap-3 mt-8">
            <Button variant="secondary" className="flex-1" onClick={() => setIsRejectModalOpen(false)}>Cancel</Button>
            <Button 
              variant="danger" 
              className="flex-1" 
              type="submit"
              isLoading={isProcessing}
            >
              Confirm Rejection
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default BulkTransferPage;
